import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ImageIcon,
  Infinity,
  Lock,
  ShieldCheck,
  UserCheck,
  Users,
} from 'lucide-react'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { LoginSignupBackdrop } from 'src/pages/LoginSignup/components/LoginSignupBackdrop'
import { LoginSignupPanel } from 'src/pages/LoginSignup/LoginSignupPanel'
import { SignupSuccessPanel } from 'src/pages/SignupSuccess/SignupSuccessPanel'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Dialog, DialogContent, DialogTitle } from 'src/shared/components/ui/dialog'
import { Image } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const FALLBACK_PREVIEWS = [
  '/demo/hoodie-mockup.png',
  '/demo/tee-mockup.png',
  '/demo/tote-mockup.png',
]

const TREYD_LOGO =
  'https://www.datocms-assets.com/67452/1720015305-group-37300.svg'

const preventDismiss = (event: { preventDefault: () => void }) => {
  event.preventDefault()
}

const TEASER_CARD_CLASS = [
  '-rotate-6 translate-y-3',
  'rotate-2 -translate-y-1 z-10',
  '-rotate-3 translate-y-4',
] as const

const MockupTeaser = ({ urls }: { urls: string[] }) => {
  const previews = urls.slice(0, 3)

  return (
    <div className="relative flex min-h-[220px] flex-1 items-center justify-center">
      <Row className="items-center justify-center -space-x-8">
        {previews.map((url, index) => (
          <div
            key={url}
            className={cn(
              'w-32 shrink-0 overflow-hidden rounded-xl border border-white bg-white p-1.5 shadow-md ring-1 ring-black/5',
              TEASER_CARD_CLASS[index] ?? 'rotate-2'
            )}
          >
            <div className="aspect-[4/5] overflow-hidden rounded-md bg-white">
              <Image
                src={url}
                alt=""
                className="h-full w-full scale-110 object-cover blur-sm"
              />
            </div>
          </div>
        ))}
      </Row>
      <Column className="absolute inset-0 z-20 items-center justify-center">
        <Column className="size-12 items-center justify-center rounded-full bg-white shadow-sm">
          <Lock className="h-4 w-4 text-indigo-500" />
        </Column>
      </Column>
    </div>
  )
}

const PaywallValue = ({ urls }: { urls: string[] }) => {
  const { t } = useLocalization()

  const features = [
    {
      icon: ImageIcon,
      label: t('Your logo on every product', 'Er logga på varje produkt'),
    },
    {
      icon: Infinity,
      label: t('Unlimited mockups', 'Obegränsade mockups'),
    },
    {
      icon: UserCheck,
      label: t(
        'Human review before approval',
        'Personlig granskning innan godkännande'
      ),
    },
    {
      icon: Users,
      label: t(
        'Share and align with your team',
        'Dela och stäm av med teamet'
      ),
    },
  ]

  return (
    <Column className="min-w-0 flex-1 justify-between overflow-y-auto bg-[hsl(220_16%_96%)] p-10">
      <Column className="min-h-0 flex-1 gap-5">
        <Row className="w-fit items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1">
          <Lock className="h-3 w-3 text-violet-600" />
          <span className="text-xs text-violet-700">
            {t('Your mockups are ready', 'Era mockups är klara')}
          </span>
        </Row>

        <Column className="gap-2">
          <h2 className="font-semibold">
            {t('See your designs ', 'Se era designs ')}
            <span className="text-indigo-500">
              {t('come to life', 'komma till liv')}
            </span>
          </h2>
          <p className="text-muted-foreground">
            {t(
              'Unlock high-quality mockups with your logo, curated products and kit suggestions.',
              'Lås upp högkvalitativa mockups med er logga, utvalda produkter och kitförslag.'
            )}
          </p>
        </Column>

        <MockupTeaser urls={urls} />

        <Row className="gap-4">
          {features.map((feature) => (
            <Column key={feature.label} className="min-w-0 flex-1 items-center gap-2 text-center">
              <feature.icon className="h-4 w-4 text-indigo-400" />
              <span className="text-center text-xs leading-snug text-muted-foreground">
                {feature.label}
              </span>
            </Column>
          ))}
        </Row>
      </Column>

      <Column className="items-center gap-3 pt-6">
        <Row className="items-center justify-center gap-2">
          <ShieldCheck
            className="size-5 shrink-0 text-blue-500 stroke-none [&>path:first-of-type]:fill-current [&>path:first-of-type]:stroke-none [&>path:last-of-type]:fill-none [&>path:last-of-type]:stroke-white [&>path:last-of-type]:stroke-[2]"
            strokeWidth={0}
            aria-hidden
          />
          <p className="text-muted-foreground">
            {t(
              'Trusted by leading companies since 2015',
              'Betrodd av ledande företag sedan 2015'
            )}
          </p>
        </Row>
        <Row className="flex-wrap items-center justify-center gap-x-6 gap-y-3">
          <img
            src={IMAGE_URLS.logos.ica}
            alt="ICA"
            className="h-[1.3rem] opacity-50 [filter:brightness(0)]"
          />
          <img
            src={IMAGE_URLS.logos.luminar}
            alt="Luminar"
            className="h-[1.5rem] opacity-50 [filter:brightness(0)]"
          />
          <img
            src={TREYD_LOGO}
            alt="Treyd"
            className="h-[1.3rem] opacity-50 [filter:brightness(0)]"
          />
          <img
            src={IMAGE_URLS.logos.rift}
            alt="Rift"
            className="h-[1.9rem] opacity-50 [filter:brightness(0)]"
          />
        </Row>
      </Column>
    </Column>
  )
}

export const MockupLeadPopup = ({
  open,
  onOpenChange,
  nextPath,
  previewUrls = [],
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  nextPath: string
  previewUrls?: string[]
}) => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const [step, setStep] = useState<'login-signup' | 'signup-success'>(
    'login-signup'
  )
  const [orgId, setOrgId] = useState<string | null>(null)
  const [isLoginMode, setIsLoginMode] = useState(false)
  const teaseUrls = previewUrls.length > 0 ? previewUrls : FALLBACK_PREVIEWS

  useEffect(() => {
    if (!open) {
      setStep('login-signup')
      setOrgId(null)
      setIsLoginMode(false)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true)
      }}
    >
      <DialogContent
        showClose={false}
        onPointerDownOutside={preventDismiss}
        onInteractOutside={preventDismiss}
        onEscapeKeyDown={preventDismiss}
        overlayClassName="bg-theme_light backdrop-blur-none duration-700 ease-out"
        className="z-[200] w-full max-w-5xl gap-0 overflow-visible rounded-none border-0 !bg-transparent p-0 shadow-none duration-700 ease-out data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0"
      >
        <DialogTitle className="sr-only">
          {t('Your mockups are ready', 'Era mockups är klara')}
        </DialogTitle>

        <div className="pointer-events-none fixed inset-0 z-[95] overflow-hidden">
          <LoginSignupBackdrop />
        </div>

        {step === 'login-signup' ? (
          <LoginSignupPanel
            loginModeSource="controlled"
            isLoginMode={isLoginMode}
            onLoginModeChange={setIsLoginMode}
            nextPath={nextPath}
            signupTitle={t('Create a free account', 'Skapa ett gratiskonto')}
            signupDescription={t(
              'Sign up to unlock your mockups, quotes and ongoing kits.',
              'Registrera er för att låsa upp era mockups, offerter och pågående kit.'
            )}
            className="w-full max-h-[min(90vh,800px)]"
            sideContent={<PaywallValue urls={teaseUrls} />}
            onSignupOrgReady={(id) => {
              setOrgId(id)
              setStep('signup-success')
            }}
          />
        ) : (
          <SignupSuccessPanel
            orgId={orgId}
            variant="embedded"
            onSuccess={() => navigate(nextPath)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
